import React, { useState, useCallback } from 'react';
import { Globe, ChevronDown, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ClientFullData, ClientStage, ClientHealthMetrics } from '@/types/client';
import { getWarmthLevel } from '@/types/client';
import { useUpdateClientStage } from '@/hooks/useClients';
import { ALL_STAGES, STAGE_LABELS, STAGE_COLORS, WARMTH_COLORS, WARMTH_LABELS } from '../shared/stageConstants';
import { LogCommunicationForm } from './LogCommunicationForm';

interface ProfileHeaderProps {
  client: ClientFullData;
  healthMetrics: ClientHealthMetrics | null;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ client, healthMetrics }) => {
  const updateStageMutation = useUpdateClientStage();
  const currentStage = ((client as Record<string, unknown>).stage as ClientStage) || 'active';
  const stageColors = STAGE_COLORS[currentStage];
  const warmth = getWarmthLevel(healthMetrics?.warmth_ratio ?? null);
  const warmthColors = WARMTH_COLORS[warmth];

  const handleStageChange = useCallback(
    (stage: ClientStage) => {
      if (client.id && stage !== currentStage) {
        updateStageMutation.mutate({ clientId: client.id, stage });
      }
    },
    [client.id, currentStage, updateStageMutation]
  );

  return (
    <div className="bg-surface-1 border border-border rounded p-4 space-y-3">
      {/* Top row: name + stage badge + warmth */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl md:text-2xl uppercase tracking-wider text-foreground truncate">
            {client.name}
          </h1>
          {client.domain && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
              <Globe className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{client.domain}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Stage dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'gap-1.5 rounded px-2.5 py-1 text-xs font-label uppercase tracking-wider',
                  stageColors.bg,
                  stageColors.text,
                  'border border-current/20 hover:brightness-110'
                )}
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', stageColors.dot)} />
                {STAGE_LABELS[currentStage]}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-surface-1 border-border">
              {ALL_STAGES.map((stage) => (
                <DropdownMenuItem
                  key={stage}
                  onClick={() => handleStageChange(stage)}
                  className={cn(
                    'gap-2 text-xs font-label uppercase tracking-wider cursor-pointer',
                    stage === currentStage && 'bg-surface-2'
                  )}
                >
                  <span className={cn('w-1.5 h-1.5 rounded-full', STAGE_COLORS[stage].dot)} />
                  {STAGE_LABELS[stage]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Warmth indicator */}
          <div
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-label uppercase tracking-wider',
              warmthColors.bg,
              warmthColors.text,
              'border border-current/20'
            )}
          >
            <span className={cn('w-1.5 h-1.5 rounded-full', warmthColors.dot)} />
            {WARMTH_LABELS[warmth]}
          </div>
        </div>
      </div>

      {/* Quick actions row */}
      <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
        <Button
          variant="default"
          size="sm"
          className="text-xs"
          disabled
          title="Coming soon"
        >
          New Booking
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs gap-1"
          disabled
          title="Coming soon"
        >
          <FileText className="h-3.5 w-3.5" />
          Add Note
        </Button>
        <LogCommunicationForm
          clientId={client.id!}
          trigger={
            <Button variant="outline" size="sm" className="text-xs">
              Log Comms
            </Button>
          }
        />
      </div>
    </div>
  );
};
